import React from 'react';

import { AutoCompletePrediction } from '@opendoor/cinderblocks/core/AddressInput';
import { IAddressInputAddress } from '@opendoor/cinderblocks/growth/AddressInput/AddressInput';
import { ADDRESS_INVALID_RESULT as ADDRESS_INVALID_RESULT_CINDERBLOCKS } from '@opendoor/cinderblocks/helpers/addressValidation';

import {
  OdProtosCasaData_Result,
  OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product,
  OdProtosSellReceptionData_SellerInput_Channel,
} from '__generated__/athena';

import { startAddressVerification } from '../components/api/addressInputHelpers';
import { handleAddressVerificationSuccess } from '../components/shared/SellerFlowAddressInput';
import { setCookie } from '../components/shared/trackers';
import { useObservability } from './observability';
import useQueryParams from './queryParams';

export type SelectedAddress = IAddressInputAddress & {
  id?: string;
  result?: OdProtosCasaData_Result[];
  source?: 'google' | 'casa';
};

export const ADDRESS_INVALID_RESULT: OdProtosCasaData_Result[] =
  ADDRESS_INVALID_RESULT_CINDERBLOCKS as OdProtosCasaData_Result[];

interface OnboardingFlowInput {
  address: SelectedAddress;
  product: OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product;
  trackEvent: ReturnType<typeof useObservability>['trackEvent'];
  trackingTaxonomy?: string;
  channel?: OdProtosSellReceptionData_SellerInput_Channel;
  trackingKeywords?: string;
  inputLocation?: string;
  onSuccess?: (address: IAddressInputAddress, token: string, pdpUrl?: string) => void;
  partnerReferralId?: string;
  pageSetPath?: string;
  addressUUID?: string;
  canRedirect?: boolean;
}

export const gateProceedToOnboardingFlow = async ({
  address,
  product,
  trackEvent,
  trackingTaxonomy,
  channel,
  trackingKeywords,
  inputLocation,
  onSuccess,
  partnerReferralId,
  pageSetPath,
  addressUUID,
  canRedirect = true,
}: OnboardingFlowInput) => {
  if (trackingTaxonomy) {
    setCookie('sourceForTracking', trackingTaxonomy, 1);
  }

  const error = await startAddressVerification({
    addressUUID,
    address,
    product,
    channel,
    trackingKeywords,
    inputLocation,
    onSuccess: (token, pdpUrl, pageSetPath) => {
      handleAddressVerificationSuccess({
        address,
        token,
        trackEvent,
        pdpUrl,
        pageSetPath,
        onSuccess,
      });
    },
    partnerReferralId,
    pageSetPath,
  });

  if (error) {
    if (error.type === 'invalid-address') {
      if (canRedirect) {
        window.location.href = '/manual-address-entry';
      } else {
        throw new Error(error.message);
      }
    } else {
      window.location.href = '/error';
    }
  }
};

export type AddressValidationContextProps = {
  validatedAddress?: SelectedAddress;
  setValidatedAddress: React.Dispatch<React.SetStateAction<SelectedAddress | undefined>>;
  addressResults: OdProtosCasaData_Result[];
  setAddressResults: React.Dispatch<React.SetStateAction<OdProtosCasaData_Result[]>>;
  isPredictionSelectLoading: boolean;
  setIsPredictionSelectLoading: React.Dispatch<React.SetStateAction<boolean>>;
  firstPrediction: AutoCompletePrediction | null;
  setFirstPrediction: React.Dispatch<React.SetStateAction<AutoCompletePrediction | null>>;
  partnerReferralId?: string;
  setPartnerReferralId: React.Dispatch<React.SetStateAction<string | undefined>>;
  pageSetPath?: string;
  setPageSetPath: React.Dispatch<React.SetStateAction<string | undefined>>;
  trackingKeywords?: string;
  setTrackingKeywords: React.Dispatch<React.SetStateAction<string | undefined>>;
  channel?: OdProtosSellReceptionData_SellerInput_Channel;
  setChannel: React.Dispatch<
    React.SetStateAction<OdProtosSellReceptionData_SellerInput_Channel | undefined>
  >;
};

const AddressValidationContext = React.createContext<AddressValidationContextProps>({
  validatedAddress: undefined,
  setValidatedAddress: () => null,
  addressResults: [],
  setAddressResults: () => null,
  isPredictionSelectLoading: false,
  setIsPredictionSelectLoading: () => null,
  firstPrediction: null,
  setFirstPrediction: () => null,
  partnerReferralId: undefined,
  setPartnerReferralId: () => null,
  pageSetPath: undefined,
  setPageSetPath: () => null,
  trackingKeywords: undefined,
  setTrackingKeywords: () => null,
  channel: undefined,
  setChannel: () => null,
});

export const useAddressValidationContext = () => React.useContext(AddressValidationContext);

export const AddressValidationContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    address,
    result,
    pageSetPath: pageSetPathQueryParam,
    trackingKeyword: trackingKeyworksQueryParam,
    partnerReferralId: partnerReferralIdQueryParam,
    channel: channelQueryParam,
  } = useQueryParams();
  const [validatedAddress, setValidatedAddress] = React.useState<SelectedAddress | undefined>(
    () => {
      if (!address) return undefined;
      return JSON.parse(address as string);
    },
  );

  const [addressResults, setAddressResults] = React.useState<OdProtosCasaData_Result[]>(() => {
    if (!result) return [];
    if (Array.isArray(result)) {
      return result as OdProtosCasaData_Result[];
    }
    return [result as OdProtosCasaData_Result];
  });
  const [isPredictionSelectLoading, setIsPredictionSelectLoading] = React.useState(false);
  const [firstPrediction, setFirstPrediction] = React.useState<AutoCompletePrediction | null>(null);
  const [partnerReferralId, setPartnerReferralId] = React.useState<string | undefined>(() => {
    if (!partnerReferralIdQueryParam) return undefined;
    return partnerReferralIdQueryParam as string;
  });
  const [pageSetPath, setPageSetPath] = React.useState<string | undefined>(() => {
    if (!pageSetPathQueryParam) return undefined;
    return pageSetPathQueryParam as string;
  });
  const [trackingKeywords, setTrackingKeywords] = React.useState<string | undefined>(() => {
    if (!trackingKeyworksQueryParam) return undefined;
    return trackingKeyworksQueryParam as string;
  });
  const [channel, setChannel] = React.useState<
    OdProtosSellReceptionData_SellerInput_Channel | undefined
  >(() => {
    if (!channelQueryParam) return 'DEFAULT';
    return channelQueryParam as OdProtosSellReceptionData_SellerInput_Channel;
  });
  return (
    <AddressValidationContext.Provider
      value={{
        trackingKeywords,
        setTrackingKeywords,
        pageSetPath,
        setPageSetPath,
        validatedAddress,
        setValidatedAddress,
        addressResults,
        setAddressResults,
        isPredictionSelectLoading,
        setIsPredictionSelectLoading,
        firstPrediction,
        setFirstPrediction,
        partnerReferralId,
        setPartnerReferralId,
        channel,
        setChannel,
      }}
    >
      {children}
    </AddressValidationContext.Provider>
  );
};
